<script setup lang="ts">
import { computed, type PropType } from 'vue';
import Dialog from 'primevue/dialog';
import { DateTime } from 'luxon';
import type { PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanFrames from './PerfusionScanFrames.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  scan: {
    type: Object as PropType<PerfusionScanModel | undefined>,
    default: undefined
  },
  /** The investigation the perfusion belongs to, needed to fetch the captured frames. */
  pumpCenterId: {
    type: String,
    default: ''
  },
  perfusionId: {
    type: String,
    default: ''
  },
  /** Case label, so it's clear which perfusion the readings belong to. */
  perfusionLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible']);

const DATE_TIME_24H_FORMAT = 'dd MMM yyyy, HH:mm';

const formatDate = (date?: Date) =>
  date ? DateTime.fromJSDate(date).toFormat(DATE_TIME_24H_FORMAT) : '—';

const values = computed(() => props.scan?.values ?? []);
const extraValues = computed(() => props.scan?.extraValues ?? []);
const errorValues = computed(() => props.scan?.errorValues ?? []);

const details = computed(() => {
  const scan = props.scan;
  if (!scan) {
    return [];
  }

  const rows = [
    { label: 'Machine', value: scan.machineName },
    { label: 'Captured', value: formatDate(scan.capturedAt) },
    { label: 'Uploaded', value: formatDate(scan.createdAt) },
    { label: 'Status', value: scan.status }
  ];

  if (scan.pumpCenter) {
    rows.push({ label: 'Pump center', value: scan.pumpCenter });
  }

  if (scan.modelId) {
    rows.push({ label: 'Model', value: scan.modelId });
  }

  return rows;
});

</script>

<template>
  <Dialog :visible="visible"
    modal
    dismissable-mask
    :draggable="false"
    :style="{ width: '520px' }"
    :breakpoints="{ '600px': '92vw' }"
    @update:visible="emit('update:visible', $event)">
    <template #header>
      <div class="scan-detail__header">
        <FontAwesomeIcon :icon="['far', 'monitor-waveform']" class="scan-detail__header-icon"/>
        <div class="scan-detail__header-text">
          <span class="scan-detail__title">{{ scan?.machineName || 'Scan' }}</span>
          <span class="scan-detail__subtitle" v-if="perfusionLabel">{{ perfusionLabel }}</span>
        </div>
      </div>
    </template>

    <div class="scan-detail" v-if="scan">
      <dl class="scan-detail__meta">
        <div class="scan-detail__meta-row" v-for="detail in details" :key="detail.label">
          <dt>{{ detail.label }}</dt>
          <dd>{{ detail.value }}</dd>
        </div>
      </dl>

      <section class="scan-detail__section">
        <h3 class="scan-detail__section-title">Collected values</h3>
        <table class="scan-detail__table" v-if="values.length > 0">
          <tbody>
            <tr v-for="value in values" :key="value.path">
              <th scope="row">{{ value.label }}</th>
              <td>{{ value.value }}</td>
            </tr>
          </tbody>
        </table>
        <p class="scan-detail__empty" v-else>No values were recorded for this scan.</p>
      </section>

      <section class="scan-detail__section" v-if="errorValues.length > 0">
        <h3 class="scan-detail__section-title scan-detail__section-title--error">Errors</h3>
        <table class="scan-detail__table">
          <tbody>
            <tr v-for="value in errorValues" :key="value.path">
              <th scope="row">{{ value.label }}</th>
              <td>{{ value.value }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="scan-detail__section" v-if="extraValues.length > 0">
        <h3 class="scan-detail__section-title">Additional data</h3>
        <table class="scan-detail__table">
          <tbody>
            <tr v-for="value in extraValues" :key="value.path">
              <th scope="row">{{ value.label }}</th>
              <td>{{ value.value }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="scan-detail__section" v-if="scan.frames.length > 0">
        <h3 class="scan-detail__section-title">
          Captured frames ({{ scan.frames.length }})
        </h3>
        <PerfusionScanFrames :scan="scan"
          :pump-center-id="pumpCenterId"
          :perfusion-id="perfusionId"/>
        <p class="scan-detail__hint">Click a frame to see it full size.</p>
      </section>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@use '../../../assets/variables.module' as *;

.scan-detail__header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.scan-detail__header-icon {
  font-size: 20px;
  color: hsl(210, 11%, 25%);
}
.scan-detail__header-text {
  display: flex;
  flex-direction: column;
}
.scan-detail__title {
  font-size: 16px;
  font-weight: 600;
}
.scan-detail__subtitle {
  font-size: 12px;
  color: hsl(210, 9%, 50%);
}
.scan-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.scan-detail__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  margin: 0;
}
.scan-detail__meta-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 16px;
  min-width: 0;

  dt {
    color: hsl(210, 9%, 50%);
  }

  dd {
    margin: 0;
    color: hsl(210, 11%, 25%);
    font-weight: 500;
    word-break: break-word;
  }
}
.scan-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.scan-detail__section-title {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}
.scan-detail__section-title--error {
  color: hsl(0, 65%, 45%);
}
.scan-detail__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 7px 0;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid var(--color-border);
    word-break: break-word;
  }

  th {
    width: 55%;
    font-weight: 400;
    color: hsl(210, 9%, 45%);
    padding-right: 12px;
  }

  td {
    font-weight: 600;
    color: var(--color-text);
  }

  tr:last-child th,
  tr:last-child td {
    border-bottom: none;
  }
}
.scan-detail__empty {
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}
.scan-detail__hint {
  margin: 0;
  font-size: 11px;
  color: hsl(210, 9%, 50%);
}
</style>
